import { useState } from 'react';
import * as XLSX from 'xlsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { FormField, Input } from '@/components/ui/Input.jsx';
import { Card, CardContent } from '@/components/ui/Card.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog.jsx';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table.jsx';
import { FileSpreadsheet, Eye, Loader2 } from 'lucide-react';
import { periodLabel } from '@/lib/format.js';

const MAX_PREVIEW_ROWS = 500;

function SheetTable({ headers, rows }) {
  const preview = rows.slice(0, MAX_PREVIEW_ROWS);
  return (
    <>
      <Table>
        <THead>
          <TR>{headers.map((h, i) => <TH key={i}>{h}</TH>)}</TR>
        </THead>
        <TBody>
          {preview.map((row, ri) => (
            <TR key={ri}>
              {headers.map((_, ci) => <TD key={ci}>{row[ci] ?? ''}</TD>)}
            </TR>
          ))}
        </TBody>
      </Table>
      {rows.length > MAX_PREVIEW_ROWS && (
        <p className="mt-2 text-center text-xs text-slate-500">
          Showing first {MAX_PREVIEW_ROWS} of {rows.length} rows. Download Excel for full data.
        </p>
      )}
    </>
  );
}

function ReportViewerDialog({ open, onOpenChange, title, sheets }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="fit">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {sheets.length === 1 ? (
          <SheetTable headers={sheets[0].headers} rows={sheets[0].rows} />
        ) : sheets.length > 1 ? (
          <Tabs defaultValue={sheets[0].name}>
            <TabsList>
              {sheets.map((s) => <TabsTrigger key={s.name} value={s.name}>{s.name}</TabsTrigger>)}
            </TabsList>
            {sheets.map((s) => (
              <TabsContent key={s.name} value={s.name}>
                <SheetTable headers={s.headers} rows={s.rows} />
              </TabsContent>
            ))}
          </Tabs>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ReportTile({ title, description, onView, onDownload, viewing }) {
  return (
    <Card className="hover:shadow-md transition">
      <CardContent>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="font-semibold">{title}</h4>
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" size="sm" onClick={onView} disabled={viewing}>
              {viewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              View
            </Button>
            <Button variant="outline" size="sm" onClick={onDownload}>
              <FileSpreadsheet className="h-4 w-4" />Excel
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [runId, setRunId] = useState('');
  const [viewer, setViewer] = useState({ open: false, title: '', sheets: [] });
  const [viewingKey, setViewingKey] = useState(null);
  const { data: runs = [] } = useQuery({ queryKey: ['payrollRuns'], queryFn: () => api.get('/payroll/runs') });

  const dl = (path, name) => api.download(path, name);

  const view = async (key, path, title) => {
    setViewingKey(key);
    try {
      const blob = await api.get(path);
      const wb = XLSX.read(new Uint8Array(await blob.arrayBuffer()));
      const sheets = wb.SheetNames.map((name) => {
        const data = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1 });
        return { name, headers: data[0] || [], rows: data.slice(1) };
      });
      setViewer({ open: true, title, sheets });
    } finally {
      setViewingKey(null);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Reports" description="Operational, payroll, and statutory exports" />

      <ReportViewerDialog
        open={viewer.open}
        onOpenChange={(o) => setViewer((v) => ({ ...v, open: o }))}
        title={viewer.title}
        sheets={viewer.sheets}
      />

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="payroll">Payroll</TabsTrigger>
          <TabsTrigger value="government">Government</TabsTrigger>
        </TabsList>

        <TabsContent value="employees">
          <Section title="Employee Reports">
            <div className="mb-4 grid gap-3 md:grid-cols-3">
              <FormField label="Year">
                <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
              </FormField>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <ReportTile title="Employee Master" description="All employees with key details"
                viewing={viewingKey === 'emp-master'}
                onView={() => view('emp-master', '/reports/employees/master.xlsx', 'Employee Master')}
                onDownload={() => dl('/reports/employees/master.xlsx', 'employee-master.xlsx')} />
              <ReportTile title="Headcount by Department" description="Active employee count grouped by department"
                viewing={viewingKey === 'headcount'}
                onView={() => view('headcount', '/reports/employees/headcount-by-dept.xlsx', 'Headcount by Department')}
                onDownload={() => dl('/reports/employees/headcount-by-dept.xlsx', 'headcount.xlsx')} />
              <ReportTile title="Joiners & Leavers (this year)" description="New hires and exits in the year-to-date"
                viewing={viewingKey === 'joiners'}
                onView={() => view('joiners', `/reports/employees/joiners-leavers.xlsx?from=${year}-01-01&to=${year}-12-31`, 'Joiners & Leavers')}
                onDownload={() => dl(`/reports/employees/joiners-leavers.xlsx?from=${year}-01-01&to=${year}-12-31`, 'joiners-leavers.xlsx')} />
              <ReportTile title="Leave Balances" description={`Year ${year}, all employees`}
                viewing={viewingKey === 'leave-bal'}
                onView={() => view('leave-bal', `/reports/employees/leave-balances.xlsx?year=${year}`, `Leave Balances ${year}`)}
                onDownload={() => dl(`/reports/employees/leave-balances.xlsx?year=${year}`, `leave-balances-${year}.xlsx`)} />
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="payroll">
          <Section title="Payroll Reports">
            <div className="mb-4 grid gap-3 md:grid-cols-3">
              <FormField label="Payroll Run">
                <Select value={runId} onChange={(e) => setRunId(e.target.value)}>
                  <option value="">— select run —</option>
                  {runs.map((r) => <option key={r.id} value={r.id}>{periodLabel(r.period_year, r.period_month)} ({r.status})</option>)}
                </Select>
              </FormField>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <ReportTile title="Payroll Register" description="Detailed per-employee gross/deductions/net for the selected run"
                viewing={viewingKey === 'pay-reg'}
                onView={() => runId && view('pay-reg', `/reports/payroll/register/${runId}.xlsx`, 'Payroll Register')}
                onDownload={() => runId && dl(`/reports/payroll/register/${runId}.xlsx`, 'payroll-register.xlsx')} />
              <ReportTile title="Component Summary" description="Totals by salary component for the selected run"
                viewing={viewingKey === 'comp-sum'}
                onView={() => runId && view('comp-sum', `/reports/payroll/component-summary/${runId}.xlsx`, 'Component Summary')}
                onDownload={() => runId && dl(`/reports/payroll/component-summary/${runId}.xlsx`, 'component-summary.xlsx')} />
              <ReportTile title="Loan Ledger" description="All active loans with balances"
                viewing={viewingKey === 'loan-ledger'}
                onView={() => view('loan-ledger', '/reports/payroll/loan-ledger.xlsx', 'Loan Ledger')}
                onDownload={() => dl('/reports/payroll/loan-ledger.xlsx', 'loan-ledger.xlsx')} />
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="government">
          <Section title="Government Returns">
            <div className="mb-4 grid gap-3 md:grid-cols-3">
              <FormField label="Payroll Run">
                <Select value={runId} onChange={(e) => setRunId(e.target.value)}>
                  <option value="">— select run —</option>
                  {runs.map((r) => <option key={r.id} value={r.id}>{periodLabel(r.period_year, r.period_month)} ({r.status})</option>)}
                </Select>
              </FormField>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <ReportTile title="EPF — Form C/H" description="Employee 8% + Employer 12% per member for the run"
                viewing={viewingKey === 'epf'}
                onView={() => runId && view('epf', `/reports/gov/epf/${runId}.xlsx`, 'EPF Return')}
                onDownload={() => runId && dl(`/reports/gov/epf/${runId}.xlsx`, 'epf-return.xlsx')} />
              <ReportTile title="ETF Return" description="Employer 3% contribution per member"
                viewing={viewingKey === 'etf'}
                onView={() => runId && view('etf', `/reports/gov/etf/${runId}.xlsx`, 'ETF Return')}
                onDownload={() => runId && dl(`/reports/gov/etf/${runId}.xlsx`, 'etf-return.xlsx')} />
              <ReportTile title="APIT (PAYE) Schedule" description="Tax deducted per employee for the run"
                viewing={viewingKey === 'apit'}
                onView={() => runId && view('apit', `/reports/gov/apit/${runId}.xlsx`, 'APIT Schedule')}
                onDownload={() => runId && dl(`/reports/gov/apit/${runId}.xlsx`, 'apit.xlsx')} />
            </div>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
